import { compressPayload, decompressPayload } from '@makemydeal/dr-common-utils';
import {
    buildPaymentGridBaggagePayload,
    carryForwardPaymentGridCells,
    resolvePaymentGridForScenario,
    type PaymentGrid,
    type PaymentGridBaggagePayload
} from '@makemydeal/dr-platform-shared';
import type { ISaveOffer } from '@makemydeal/dr-dash-bff-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    buildScenarioPaymentGrid as buildScenarioPaymentGridFromInput,
    buildTermId,
    selectors as offerReduxSelectors,
    type PaymentGridBuildOptions
} from '@makemydeal/dr-offer-redux';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import * as multiScenarioSelectors from '../selectors/multiScenarioSelectors';

/**
 * US2041815 / DEP F302921: builds the compressed payment grid that Manager View persists to the deal
 * baggage for Sales View / AMD to render without re-pricing.
 *
 * Compression happens **here, in the browser**, not on the save path: the grid is ~90 KB per scenario
 * raw, so compressing before the request leaves keeps that off the wire entirely. The save path stores
 * the finished payload as-is and only size-checks it.
 *
 * The grid is built from the save payload rather than from Redux directly, because the payload already
 * carries exactly what is needed — `terms.paymentData` keyed by term id, the axes on
 * `penciledInputs[offerType].paymentGridData`, and the selected term — and it is the same input the
 * save path reads for the toggle-off inline grid. Deriving both from one place is what keeps the stored
 * grid and the inline grid from drifting apart.
 */

export { buildTermId };
export type { PaymentGridBuildOptions };

export const buildScenarioPaymentGrid = (
    payload: ISaveOffer,
    cellPricedAt: Record<string, number> = {},
    columnSources: Record<number, string> = {},
    options: PaymentGridBuildOptions = {}
): PaymentGrid | undefined =>
    buildScenarioPaymentGridFromInput(
        {
            offerType: payload?.offer?.type,
            penciledInputs: payload?.offer?.terms?.penciledInputs as Record<string, any> | undefined,
            paymentData: payload?.offer?.terms?.paymentData as Record<string, any> | undefined
        },
        cellPricedAt,
        columnSources,
        options
    );

/**
 * The grid for a flex buy scenario, mapped onto the stored shape.
 *
 * Flex buy keeps its grid somewhere else and in a different shape: a row-keyed list on the
 * `flexBuyPayments` slice rather than `rows`/`columns`/`cells` under `penciledInputs`, with the axes
 * (`flexBuyTerms`, `flexBuyDownPayments`, `flexBuyPercents`) alongside it. It is mapped here rather than
 * stored natively so that one stored shape covers every deal type and a consumer needs one reader.
 *
 * Two things fall out of how flex buy prices. One response carries the **whole** grid and the reducer
 * replaces it wholesale, so there is a single timestamp for every cell instead of one per cell — the
 * cells cannot disagree about their age. And a cell's whole payment detail is already in the grid data,
 * so nothing is looked up by term id; the axis values come off each row and column directly, which is
 * also what makes carry-forward match on values rather than position.
 */
export const buildFlexBuyScenarioPaymentGrid = (scopeState: any, pricedAt?: number): PaymentGrid | undefined => {
    const gridData = offerReduxSelectors.getFlexBuyPaymentGridData(scopeState);
    const terms = offerReduxSelectors.getFlexBuyPaymentGridTerms(scopeState);
    const percents = offerReduxSelectors.getFlexBuyPaymentGridPercents(scopeState);

    if (!gridData?.length || !terms.length) return undefined;

    // The percent is part of what identifies a column: the same term priced at a different initial
    // discount is a different quote, so restoring one onto the other would relabel it.
    const columns = terms.map((term, index) => ({
        term,
        ...(percents[index] != null && { initialPaymentDiscount: percents[index] })
    }));

    const rows = gridData.map((row) => ({ dueAtSigning: row.value }));

    const cells = gridData.map((row) =>
        columns.map((_column, columnIndex) => {
            const item = row.items?.[columnIndex];
            const payment = item?.firstTermPaymentDetail?.paymentAmount;

            // A placeholder cell — still loading, or one the response came back empty for — carries no
            // quote, so it is stored undated rather than dated as if it had priced.
            if (!item || item.isLoading || item.isEmpty || payment == null) {
                return {
                    payment: null,
                    lenderCode: null,
                    sellRate: null,
                    rateType: null,
                    residual: null,
                    isSelected: false,
                    po: null
                };
            }

            return {
                payment,
                lenderCode: null,
                sellRate: null,
                rateType: null,
                residual: null,
                isSelected: Boolean(item.isSelected),
                po: item,
                ...(pricedAt !== undefined && { at: pricedAt })
            };
        })
    );

    return { rows, columns, cells };
};

/**
 * The grid item's content for one save: one separately compressed entry per scenario.
 *
 * Entries are keyed by **scenario (scope) id**, not by deal version. The browser cannot key by deal
 * version because DXG assigns it during the save, after this payload is built — the save path rewrites
 * each `id` to its `dealXgVersionId` once it knows them, touching only that plain-JSON field and never
 * the compressed bytes.
 *
 * Returns `undefined` when no scenario produced a grid, so the caller simply omits the field and the
 * save proceeds untouched.
 */
export const buildPaymentGridBaggage = (
    scenarios: {
        id: string;
        payload: ISaveOffer;
        cellPricedAt?: Record<string, number>;
        columnSources?: Record<number, string>;
        flexBuy?: { scopeState: any; pricedAt?: number };
        isRedesignEnabled?: boolean;
        isCellCashDownMatchEnabled?: boolean;
        /** That scenario's previously stored grid, decompressed, when the deal was loaded with one. */
        storedGrid?: PaymentGrid;
    }[]
): PaymentGridBaggagePayload | undefined => {
    if (!Array.isArray(scenarios) || !scenarios.length) return undefined;

    const withGrids = scenarios.map((scenario) => {
        // A flex buy scenario has no grid under `penciledInputs` to build from, so the two are
        // alternatives rather than a fallback: whichever grid the scenario actually has is the one stored.
        const built = scenario.flexBuy
            ? buildFlexBuyScenarioPaymentGrid(scenario.flexBuy.scopeState, scenario.flexBuy.pricedAt)
            : buildScenarioPaymentGrid(scenario.payload, scenario.cellPricedAt, scenario.columnSources, {
                  isRedesignEnabled: scenario.isRedesignEnabled,
                  isCellCashDownMatchEnabled: scenario.isCellCashDownMatchEnabled
              });

        /**
         * Cells that did not reprice keep the quote they already had.
         *
         * This is what makes the grid survive a reload, and it has to be per cell rather than per
         * scenario. A save reprices whatever the dealer touched — often one column — and everything else
         * is only in state as a restored cell, not in `terms.paymentData`, which holds the active term
         * alone after a load. Deciding fresh-or-not per scenario stored one populated column and blanked
         * the rest, while `oldestAt` took the brand-new stamp so the consumer read the grid as current.
         *
         * Matching is on axis values, never position, so an edited term starts unpriced instead of
         * inheriting the previous term's quote.
         */
        return { ...scenario, grid: carryForwardPaymentGridCells(built, scenario.storedGrid) };
    });

    /**
     * A grid is only stored when some cell of it is timestamped: an undated grid's freshness is
     * unknowable, and storing it would present a quote of unknown age as current. After the merge above,
     * a scenario that repriced nothing but had a stored grid is fully dated and stores unchanged — which
     * is why no byte-copying is needed on the save path.
     */
    const hasDatedCell = (grid?: PaymentGrid) => Boolean(grid?.cells.some((row) => row.some((cell) => cell.at !== undefined)));

    return buildPaymentGridBaggagePayload(
        withGrids.filter(({ grid }) => hasDatedCell(grid)),
        compressPayload
    );
};

/**
 * US2041815: turns a stored grid back into the shape flex buy keeps in state.
 *
 * The inverse of `buildFlexBuyScenarioPaymentGrid`, and the reason `po` holds the whole cell: the stored
 * cell is the flex buy grid item itself, so it is handed back rather than reconstructed from parts.
 *
 * The axes come from the stored rows and columns rather than from whatever is in state, so a grid saved
 * against one set of terms cannot be rendered under another — the values travel with the cells they
 * describe.
 *
 * Returns `undefined` when the stored grid holds no flex buy cell at all, which is what a grid stored for
 * some other deal type looks like from here.
 */
export const restoreFlexBuyGridFromStored = (
    grid: PaymentGrid | undefined
):
    | { flexBuyPaymentGridData: any[]; flexBuyTerms: number[]; flexBuyPercents: number[]; flexBuyDownPayments: number[] }
    | undefined => {
    if (!grid?.rows?.length || !grid.columns?.length || !grid.cells?.length) return undefined;

    const hasFlexBuyCell = grid.cells.some((row) => row?.some((cell) => (cell?.po as any)?.firstTermPaymentDetail));
    if (!hasFlexBuyCell) return undefined;

    const flexBuyTerms = grid.columns.map(({ term }) => term);

    /**
     * Positional, not filtered. `flexBuyPercents` is indexed by column — `getFlexBuyPaymentGridPercentsList`
     * turns it into `{ key: columnIndex, val }` — and the type allows holes, so a grid where only some
     * columns carry a discount must keep its gaps. Filtering them out shifted every later percent onto the
     * wrong term. Collapsed to empty when no column has one, which is how a grid with no discounts is held.
     */
    const percents = grid.columns.map((column) => (column as any).initialPaymentDiscount);
    const flexBuyPercents = percents.some((value) => value != null) ? percents : [];

    const flexBuyDownPayments = grid.rows.map(({ dueAtSigning }) => dueAtSigning);

    // Driven by the stored cells rather than the stored rows: these bytes came out of a deal, so a row
    // count that disagrees with the cell count is possible, and iterating the cells cannot walk off them.
    const flexBuyPaymentGridData = grid.cells.map((row, rowIndex) => ({
        key: rowIndex,
        value: grid.rows[rowIndex]?.dueAtSigning,
        items: grid.columns.map((column, columnIndex) => {
            const cell = row?.[columnIndex];
            // A cell that was stored unpriced comes back as the placeholder the grid renders while empty,
            // rather than as a cell claiming a payment it never had.
            if (!cell?.po)
                return { id: `${rowIndex}`, term: column.term, downPayment: grid.rows[rowIndex]?.dueAtSigning, isEmpty: true };
            return { ...(cell.po as any), isSelected: Boolean(cell.isSelected) };
        })
    }));

    return { flexBuyPaymentGridData, flexBuyTerms, flexBuyPercents, flexBuyDownPayments };
};

/**
 * The grid wrapper for a save, or `undefined` when the toggle is off or nothing could be built.
 *
 * Called from the save dispatch rather than from `buildMultipleOfferSavePayload`, deliberately.
 * `currentOfferHasUnsavedChanges` compares two builds of that payload with a deep structural diff, and
 * this field is time-dependent — a fresh `at` per build changes the gzip bytes every time — so
 * attaching it there reported unsaved changes forever and gzipped every scenario's grid on a
 * `useSelector` path, on every dispatched action.
 */
export const buildPaymentGridBaggageForSave = (
    state: StateTree,
    offerSavePayload: ISaveOffer
): PaymentGridBaggagePayload | undefined => {
    if (!featureToggleSelectors.isPaymentGridBaggageCompressionEnabled(state)) {
        return undefined;
    }

    // The primary scope id is read from state rather than `offer.meta`, which is only populated on
    // multi-scenario saves.
    // Cast as `buildMultipleOfferSavePayload` does: the scoped store types its own root shape, which
    // StateTree does not structurally satisfy even though it is the same object at runtime.
    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state as any);
    const alternates = offerSavePayload.comparisonInfo?.alternateScenarios ?? [];
    // One clock for the whole save, so two scenarios stored together cannot be judged against different ones.
    const now = Date.now();

    /**
     * Everything time-dependent comes from the scenario's own scope, since each prices independently.
     *
     * The offer type is passed in from the payload rather than letting the selector read the scope's
     * current one: state spells it `offer.offerType` while the save payload spells it `offer.type`, and
     * the grid is built from the payload's. Letting the two be resolved independently means a
     * disagreement reads another offer type's stamps, every cell comes back unstamped, and the scenario
     * is silently dropped — so both halves are keyed off the one field.
     *
     * A flex buy scenario is recognised here rather than inside the builder, because that takes reading
     * the scope's own toggle: persistence honours the flex buy grid's enablement toggle, so a deal with
     * the grid switched off stores nothing for it even if data is still lying in state.
     */
    const sourceFor = (scopeId: string, offerType?: OfferType) => {
        // Every scenario, primary included, is read through its scope — the same way
        // `buildMultipleOfferSavePayload` resolves the payload this augments.
        const scopeState = scopedStateSelectors.getScopeStateById(state as any, scopeId);
        if (!scopeState) return { cellPricedAt: {} };

        /**
         * Both conditions, not either: `paymentFlexBuyGridToggle` says the flex buy grid is switched on,
         * which is not the same as this deal being a flex buy deal. A deal that has since moved off flex
         * buy can still be carrying that toggle, and treating it as flex buy would read an empty flex buy
         * slice and throw away the regular grid it does have.
         */
        const isFlexBuyGrid =
            offerReduxSelectors.isFlexBuyPayment(scopeState as any) &&
            offerReduxSelectors.getFlexBuyPaymentGridToggle(scopeState as any);

        if (isFlexBuyGrid) {
            return {
                cellPricedAt: {},
                flexBuy: {
                    scopeState,
                    pricedAt: offerReduxSelectors.getFlexBuyPaymentGridPricedAt(scopeState as any)
                }
            };
        }

        return {
            cellPricedAt: offerType ? offerReduxSelectors.getPaymentGridCellPricedAt(scopeState as any, offerType) : {},
            columnSources: offerType ? offerReduxSelectors.getPaymentGridColumnSources(scopeState as any, offerType) : {}
        };
    };

    /**
     * The grid this scenario was loaded with, so cells that did not reprice can be carried forward.
     *
     * Resolved through the shared helper rather than reaching into the payload by hand: it owns the
     * entry lookup, the freshness verdict and the fallback to a comparison entry's legacy grid, and
     * having one implementation is what stops Manager View and Sales View disagreeing about the same
     * stored grid. An unusable entry resolves to no grid, so nothing undatable is carried forward.
     */
    const storedGridFor = (payload: ISaveOffer): PaymentGrid | undefined => {
        const version = payload.offer?.dealExchangeVersionId as string | undefined;
        if (!version) return undefined;

        const { grid, freshness } = resolvePaymentGridForScenario(
            { scenario: { id: version }, payload: multiScenarioSelectors.getPaymentGridBaggagePayload(state), now },
            decompressPayload
        );

        return freshness === 'unusable' ? undefined : grid;
    };

    const scenarios = [
        { id: primaryScopeId as string, payload: offerSavePayload },
        ...alternates.map((payload) => ({ id: payload.meta?.scenarioId as string, payload }))
    ]
        .filter(({ id, payload }) => Boolean(id) && Boolean(payload))
        .map(({ id, payload }) => ({
            id,
            payload,
            ...sourceFor(id, payload.offer?.type as OfferType),
            isRedesignEnabled: offerReduxSelectors.isAspPaymentGridRedesignActive(state, payload.offer?.type as OfferType),
            isCellCashDownMatchEnabled: featureToggleSelectors.isGridCellCashDownMatchEnabled(state),
            storedGrid: storedGridFor(payload)
        }));

    return buildPaymentGridBaggage(scenarios);
};
